import type { ReactNode } from "react";

import { Button, Flex, Input, Space } from "antd";
import clsx from "clsx";
import { cloneElement, isValidElement } from "react";
import { useTranslation } from "react-i18next";
import {
  AiOutlineAppstore,
  AiOutlineSearch,
  AiOutlineUnorderedList,
} from "react-icons/ai";

import { alignment, cls, screenFlags } from "design-system/helpers/layout";
import { useScreenSize } from "design-system/hooks/";

import type { DataTableProps } from "../types";

import s from "./Header.module.css";

type Props = Pick<
  DataTableProps,
  | "addNewAction"
  | "rightActions"
  | "searchInput"
  | "viewMode"
  | "setViewMode"
  | "updateQuery"
> & {
  customizableColumns?: DataTableProps["customizableColumns"] & {
    onClick: () => void;
  };
  title?: ReactNode;
};

const Header = ({
  addNewAction,
  rightActions,
  searchInput,
  viewMode,
  setViewMode,
  customizableColumns,
  updateQuery,
  title,
}: Props) => {
  const { t } = useTranslation(["common"]);
  const size = useScreenSize();

  let addNewButton: ReactNode = null;

  if (addNewAction) {
    if (typeof addNewAction.render === "function") {
      addNewButton = addNewAction.render();
    } else {
      addNewButton = (
        <Button
          type="primary"
          icon={addNewAction.icon}
          onClick={addNewAction.onClick}
          disabled={addNewAction.disabled}
          style={addNewAction.style}
          className={addNewAction.className ?? s.btn}
          data-testid={addNewAction.testId ?? "add-new-button"}
        >
          {addNewAction.label ?? t("components.buttons.addNew")}
        </Button>
      );
    }
  }

  return (
    <Flex
      align={alignment[size]}
      vertical={size === "md" ? false : screenFlags[size]}
      justify="space-between"
      gap="small"
      className={clsx({
        [s.marginBottomMd]: true,
        [s.width100]: cls[size],
      })}
    >
      {title}
      {searchInput && (
        <Space.Compact
          className={clsx({
            [s.searchInput]: true,
            [s.width100]: cls[size],
          })}
        >
          <Input
            data-testid={searchInput.testId ?? "search-input"}
            placeholder={searchInput.placeholder ?? t("labels.search")}
            allowClear={searchInput.allowClear}
            value={searchInput.value}
            onChange={e => {
              searchInput.onChange(e.target.value);
              updateQuery?.(e.target.value);
            }}
          />
          {searchInput.showSearchIcon && (
            <Button
              icon={<AiOutlineSearch />}
              aria-label={t("labels.search")}
            />
          )}
        </Space.Compact>
      )}
      {(addNewButton || rightActions || viewMode || customizableColumns) && (
        <Flex gap="small" className={s.rightActions}>
          {customizableColumns && (
            <Flex gap="small" className={s.addNewBtnContainer}>
              <Button
                type="default"
                icon={customizableColumns.icon}
                onClick={customizableColumns.onClick}
                style={customizableColumns.style}
                className={customizableColumns.className ?? s.btn}
                data-testid={
                  customizableColumns.testId ?? "customize-columns-button"
                }
                disabled={customizableColumns.disabled}
              >
                {customizableColumns.label ??
                  t("components.dataTable.buttons.customizeColumns")}
              </Button>
            </Flex>
          )}
          {addNewButton && (
            <Flex gap="small" className={s.addNewBtnContainer}>
              {addNewButton}
            </Flex>
          )}
          {rightActions && (
            <Flex gap="small">
              {rightActions.map((action, index) => {
                if (isValidElement(action)) {
                  return cloneElement(action, { key: action.key ?? index });
                }
                return <span key={index}>{action}</span>;
              })}
            </Flex>
          )}
          {viewMode && setViewMode && (
            <Flex gap="small">
              <Flex gap="small" className={s.viewToggle}>
                <Button
                  type={viewMode === "grid" ? "primary" : "default"}
                  icon={<AiOutlineAppstore />}
                  onClick={() => setViewMode("grid")}
                  className={s.viewToggleBtn}
                  data-testid="grid-view-button"
                  aria-pressed={viewMode === "grid"}
                />
                <Button
                  type={viewMode === "list" ? "primary" : "default"}
                  icon={<AiOutlineUnorderedList />}
                  onClick={() => setViewMode("list")}
                  className={s.viewToggleBtn}
                  data-testid="list-view-button"
                  aria-pressed={viewMode === "list"}
                />
              </Flex>
            </Flex>
          )}
        </Flex>
      )}
    </Flex>
  );
};

export default Header;
